import { useEffect, useRef } from 'react'

import type { CreatorIntegrationsApi } from '@fourthwall/services/dist/CreatorIntegrationsApiClient'

import { useKitConfigurationQuery } from '../queries/useKitConfigurationQuery'
import { useKitCreateSyncMutation } from '../queries/useKitCreateSyncMutation'
import { useKitSyncStatusQuery } from '../queries/useKitSyncStatusQuery'

export const useKit = () => {
  const { kitSyncStatusQuery, invalidateKitSyncStatusQuery } = useKitSyncStatusQuery()
  const { kitConfigurationQuery } = useKitConfigurationQuery()
  const { createKitSyncMutation } = useKitCreateSyncMutation()
  const pollRef = useRef<NodeJS.Timeout | null>(null)

  const createSyncJob = async (
    requestBody: CreatorIntegrationsApi.CreateKitSyncJob.RequestBody,
  ) => {
    try {
      await createKitSyncMutation.mutateAsync([requestBody])
    } catch (error) {
      console.error('Failed to create Kit sync job:', error)
      throw error
    }

    invalidateKitSyncStatusQuery()
  }

  useEffect(() => {
    if (kitSyncStatusQuery.data?.activeSync) {
      pollRef.current = setInterval(() => {
        invalidateKitSyncStatusQuery()
      }, 1000)
    } else if (pollRef.current) {
      console.warn('Clearing timeout')
      clearInterval(pollRef.current)
    }
  }, [kitSyncStatusQuery.data?.activeSync])

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
      }
    }
  }, [])

  const progress = [
    kitSyncStatusQuery.data?.members
      ? { key: 'MEMBERS' as const, ...kitSyncStatusQuery.data.members }
      : null,
    kitSyncStatusQuery.data?.newsletter
      ? { key: 'NEWSLETTER' as const, ...kitSyncStatusQuery.data.newsletter }
      : null,
  ]
    .filter((item): item is NonNullable<typeof item> => item != null && item.total != null)
    .map((item) => ({
      key: item.key,
      progress: item.progress ?? 0,
      total: item.total,
    }))

  return {
    syncStatus: {
      active: kitSyncStatusQuery.data?.activeSync || false,
      recent: kitSyncStatusQuery.data?.recentSync || false,
      hasSynced: kitSyncStatusQuery.data?.hasSync || false,
      progress,
      isLoading: kitSyncStatusQuery.isLoading,
    },
    configuration: {
      dataCount: kitConfigurationQuery.data?.dataCount ?? 0,
      isLoading: kitConfigurationQuery.isLoading,
    },
    createSyncJob,
    isCreatingSyncJob: createKitSyncMutation.isLoading,
  }
}
